import { cn } from "@/app/utils/cn";
import { percentageMask } from "@/app/utils/comissionPercentage";
import { formatMoneyValue, moneyMask } from "@/app/utils/formatMoneyValue";
import { Trash } from "lucide-react";
import { useState, useEffect } from "react";
import { BasicInput } from "./BasicInput";
import { useFormContext } from "react-hook-form";
import { Button } from "./ui/button";
import { z } from "zod";

type PartnerConfig = {
  valueMask: "money" | "number" | "percentage";
  commissionMask: "percentage" | "money";
  labels: {
    value: string;
    commission: string;
    leaderCommission: string;
  };
  isSingleCommission?: boolean;
};

export const partnerConfigs: Record<string, PartnerConfig> = {
  FGTS: {
    valueMask: "money",
    commissionMask: "percentage",
    labels: {
      value: "Valor",
      commission: "Comissão Vendedor",
      leaderCommission: "Comissão Líder",
    },
  },
  CREFAZ: {
    valueMask: "number",
    commissionMask: "percentage",
    labels: {
      value: "A partir de",
      commission: "Comissão",
      leaderCommission: "Comissão Líder",
    },
  },
  SALVCAR: {
    valueMask: "money",
    commissionMask: "money",
    labels: {
      value: "A partir de",
      commission: "Comissão",
      leaderCommission: "Comissão Líder",
    },
  },
  CREFISA: {
    valueMask: "percentage",
    commissionMask: "percentage",
    labels: {
      value: "Comissão Vendedor",
      commission: "Bônus Vendedor",
      leaderCommission: "Comissão Líder",
    },
    isSingleCommission: true,
  },
  CREFISA_INSS: {
    valueMask: "percentage",
    commissionMask: "percentage",
    labels: {
      value: "Comissão Vendedor",
      commission: "Bônus Vendedor",
      leaderCommission: "Comissão Líder",
    },
    isSingleCommission: true,
  },
  PAN: {
    valueMask: "percentage",
    commissionMask: "percentage",
    labels: {
      value: "Comissão Vendedor",
      commission: "Bônus Vendedor",
      leaderCommission: "Comissão Líder",
    },
    isSingleCommission: true,
  },
  C6: {
    valueMask: "percentage",
    commissionMask: "percentage",
    labels: {
      value: "Comissão Vendedor",
      commission: "Bônus Vendedor",
      leaderCommission: "Comissão Líder",
    },
    isSingleCommission: true,
  },
  CLT: {
    valueMask: "number",
    commissionMask: "percentage",
    labels: {
      value: "A partir de",
      commission: "Comissão",
      leaderCommission: "Comissão Líder",
    },
  },
};

const INSTALLMENT_PARTNERS = ["CREFAZ", "CLT"];

export const ValuesAndCommissionsValidation = z
  .object({
    value: z
      .string()
      .min(1, "* obrigatório")
      .transform((v) => Number(v.replace(/\D/g, "")))
      .refine((v) => v >= 0, "* obrigatório"),
    commission: z
      .string()
      .min(1, "* obrigatório")
      .transform((v) => {
        return Number(v.replace(/\D/g, "")) / 100;
      })
      .refine((v) => v > 0, "* obrigatório"),
    leaderCommission: z
      .string()
      .optional()
      .transform((v) => (v ? Number(v.replace(/\D/g, "")) / 100 : 0)),
    max: z
      .string()
      .optional()
      .transform((v) =>
        v ? Number(v.replace("%", "").replace(",", ".").trim()) : undefined,
      ),
  })
  .array()
  .optional();

const formSchema = z.object({
  valuesAndCommissions: ValuesAndCommissionsValidation,
});

type FormValues = z.infer<typeof formSchema>;

interface ValuesAndCommissionsFormProps {
  initialValues?: { value: number; commission: number; max?: number }[];
  isCrefaz?: boolean;
  isOpen?: boolean;
  isSuperAdmin?: boolean;
  partner: keyof typeof partnerConfigs;
}

export default function ValuesAndCommissionsForm({
  initialValues = [],
  isOpen = true,
  partner,
  isSuperAdmin = false,
}: ValuesAndCommissionsFormProps) {
  const config = partnerConfigs[partner];
  const methods = useFormContext<FormValues>();
  const [itemsAmount, setItemsAmount] = useState(initialValues.length);
  const [hasInitialized, setHasInitialized] = useState(false);

  const formatInitialValue = (item: any) => {
    const value = config.isSingleCommission
      ? Number(item.value).toFixed(2)
      : config.valueMask === "money"
        ? formatMoneyValue(item.value)
        : String(item.value);

    const commission = config.isSingleCommission
      ? Number(item.commission).toFixed(2)
      : config.commissionMask === "money"
        ? formatMoneyValue(item.commission)
        : String(item.commission.toFixed(2));

    const leaderCommission = item.leaderCommission
      ? config.commissionMask === "money"
        ? formatMoneyValue(item.leaderCommission)
        : String(Number(item.leaderCommission).toFixed(2))
      : config.commissionMask === "money"
        ? formatMoneyValue(0)
        : "0";

    const limit = item.limit ? String(formatMoneyValue(item.limit)) : undefined;

    const max = item.max
      ? config.commissionMask === "money"
        ? formatMoneyValue(item.max)
        : String(Number(item.max).toFixed(2))
      : undefined;

    return { value, commission, leaderCommission, limit, max };
  };

  useEffect(() => {
    if (isOpen && initialValues.length > 0 && !hasInitialized) {
      const formattedValues = initialValues.map(formatInitialValue);
      methods.setValue("valuesAndCommissions", formattedValues as any);
      setItemsAmount(formattedValues.length);
      setHasInitialized(true);
    }

    if (!isOpen && hasInitialized) {
      setHasInitialized(false);
    }
  }, [isOpen, initialValues, methods, hasInitialized]);

  const handleAddItem = () => {
    const currentValues = methods.getValues("valuesAndCommissions") || [];

    methods.setValue("valuesAndCommissions", [
      ...currentValues,
      {
        value: config.isSingleCommission
          ? 0
          : config.valueMask === "money"
            ? Number(formatMoneyValue(0).replace(/\D/g, ""))
            : 0,
        commission: 0,
        leaderCommission: 0,
      },
    ]);

    updateItemsAmount();
  };

  const handleRemoveItem = (i: number) => {
    methods.setValue(
      "valuesAndCommissions",
      methods
        ?.getValues("valuesAndCommissions")
        ?.filter((_, index) => index !== i),
    );
    updateItemsAmount();
  };

  const updateItemsAmount = () => {
    setItemsAmount(methods.getValues("valuesAndCommissions")?.length || 0);
  };

  const sortItems = () => {
    const items = methods.getValues("valuesAndCommissions");
    if (!items) return;

    const sorted = items.sort((a, b) => {
      const getValue = (item: any) =>
        config.isSingleCommission
          ? Number(String(item.value).replace(",", "."))
          : +String(item.value).replace(/\D/g, "");

      return getValue(a) - getValue(b);
    });

    methods.setValue("valuesAndCommissions", sorted);
  };

  return (
    <div className="mb-6 flex w-full flex-col items-start">
      {new Array(itemsAmount).fill(0).map((_, i) => {
        const isSingleCommission = config.isSingleCommission;

        return (
          <div key={i} className="flex w-full flex-col gap-2">
            {!isSingleCommission && (
              <div className="group relative mb-4 flex w-full items-end gap-4">
                <div
                  className={cn(
                    "min-w-0 flex-1",
                    isSuperAdmin &&
                      "transition-all duration-200 group-hover:flex-[0.8]",
                  )}
                >
                  <BasicInput
                    name={`valuesAndCommissions[${i}].value`}
                    style={{ backgroundColor: "#F7F7F7" }}
                    label={i === 0 ? config.labels.value : undefined}
                    type="text"
                    onInput={
                      config.valueMask === "money" ? moneyMask : undefined
                    }
                    onBlur={sortItems}
                    disabled={!isSuperAdmin}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <BasicInput
                    name={`valuesAndCommissions[${i}].commission`}
                    style={{ backgroundColor: "#F7F7F7" }}
                    label={i === 0 ? config.labels.commission : undefined}
                    variant={
                      config.commissionMask === "money"
                        ? "default"
                        : "percentage"
                    }
                    onInput={
                      config.commissionMask === "money"
                        ? moneyMask
                        : percentageMask
                    }
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <BasicInput
                    name={`valuesAndCommissions[${i}].leaderCommission`}
                    style={{ backgroundColor: "#F7F7F7" }}
                    label={i === 0 ? config.labels.leaderCommission : undefined}
                    type="text"
                    variant={
                      config.commissionMask === "money"
                        ? "default"
                        : "percentage"
                    }
                    onInput={
                      config.commissionMask === "money"
                        ? moneyMask
                        : percentageMask
                    }
                  />
                </div>

                {!isSingleCommission && (
                  <div className="min-w-0 flex-1">
                    <BasicInput
                      name={`valuesAndCommissions[${i}].max`}
                      style={{ backgroundColor: "#F7F7F7" }}
                      label={i === 0 ? "Máximo" : undefined}
                      type="text"
                      variant={
                        config.commissionMask === "money"
                          ? "default"
                          : "percentage"
                      }
                      onInput={
                        config.commissionMask === "money"
                          ? moneyMask
                          : percentageMask
                      }
                      disabled={!isSuperAdmin}
                    />
                  </div>
                )}

                {isSuperAdmin && (
                  <div
                    className={cn(
                      "flex w-0 items-end overflow-hidden opacity-0 transition-all duration-200 group-hover:w-10 group-hover:opacity-100",
                      i === 0 ? "pb-0" : "items-center",
                    )}
                  >
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => handleRemoveItem(i)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {isSingleCommission && (
              <div className="mb-4 grid w-full grid-cols-3 items-end gap-4">
                <BasicInput
                  name={`valuesAndCommissions[${i}].value`}
                  style={{ backgroundColor: "#F7F7F7" }}
                  label={i === 0 ? "Comissão" : undefined}
                  type="text"
                  variant="percentage"
                  onInput={percentageMask}
                  onBlur={sortItems}
                />
                <BasicInput
                  name={`valuesAndCommissions[${i}].leaderCommission`}
                  style={{ backgroundColor: "#F7F7F7" }}
                  label={i === 0 ? "Comissão Líder" : undefined}
                  type="text"
                  variant="percentage"
                  onInput={percentageMask}
                />
                <BasicInput
                  name={`valuesAndCommissions[${i}].max`}
                  style={{ backgroundColor: "#F7F7F7" }}
                  label={i === 0 ? "Máximo" : undefined}
                  type="text"
                  variant="percentage"
                  onInput={percentageMask}
                  disabled={!isSuperAdmin}
                />
              </div>
            )}
          </div>
        );
      })}

      {methods.formState.errors.valuesAndCommissions?.message && (
        <p className="text-sm font-light text-error">
          {methods.formState.errors.valuesAndCommissions?.message}
        </p>
      )}

      {!config.isSingleCommission && isSuperAdmin && (
        <p
          className={cn("mt-2 cursor-pointer px-2 py-2 text-sm text-primary")}
          onClick={handleAddItem}
        >
          + Adicionar {INSTALLMENT_PARTNERS.includes(partner) ? "Parcela" : "Valor"}
        </p>
      )}
    </div>
  );
}
